import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { queryClient } from '../components/queryClient';
import { useFleet } from '../hooks/useFleet';
import { DeviceHealth } from '../interfaces';
import { FleetService } from '../services';

export const useJiraTicketsForDevices = (enabled = true) => {
  const fleet = useFleet();

  return useQuery({
    // Keep under the ['fleetHealth', fleetId] namespace so refreshFleetHealth() invalidates it too.
    queryKey: ['fleetHealth', fleet.id, 'jiraTickets'],
    queryFn: () => FleetService.getJiraTicketsForDevices(),
    enabled: enabled && !!fleet.id && !!fleet.config.device_health_config?.is_enabled,
    staleTime: 0,
  });
};

export const useFleetHealth = (enabled = true) => {
  const fleet = useFleet();
  const queryKey = ['fleetHealth', fleet.id];

  const queryFn = () => FleetService.getHealth();

  return useQuery({
    queryKey,
    queryFn,
    enabled: enabled && !!fleet.id && !!fleet.config.device_health_config?.is_enabled,
  });
};

// Per-section hooks split from useFleetHealth for performance. Each fetches one sub-endpoint independently
// so slow Influx-backed sections (last check-in, cumulative uptime) never block the rest of the page.
export const useHealthStatusesForDevices = (enabled = true) => {
  const fleet = useFleet();

  return useQuery({
    queryKey: ['fleetHealth', fleet.id, 'statuses'],
    queryFn: () => FleetService.getHealthStatusesForDevices(),
    enabled: enabled && !!fleet.id && !!fleet.config.device_health_config?.is_enabled,
  });
};

export const useBusFeedForDevices = (enabled = true) => {
  const fleet = useFleet();

  return useQuery({
    queryKey: ['fleetHealth', fleet.id, 'busFeed'],
    queryFn: () => FleetService.getBusFeedForDevices(),
    enabled: enabled && !!fleet.id && !!fleet.config.device_health_config?.is_enabled,
  });
};

export const useOTADetailsForDevices = (enabled = true) => {
  const fleet = useFleet();

  return useQuery({
    queryKey: ['fleetHealth', fleet.id, 'otaDetails'],
    queryFn: () => FleetService.getOTADetailsForDevices(),
    enabled: enabled && !!fleet.id && !!fleet.config.device_health_config?.is_enabled,
  });
};

export const useVersioningForDevices = (enabled = true) => {
  const fleet = useFleet();

  return useQuery({
    queryKey: ['fleetHealth', fleet.id, 'versioning'],
    queryFn: () => FleetService.getVersioningForDevices(),
    enabled: enabled && !!fleet.id && !!fleet.config.device_health_config?.is_enabled,
  });
};

export const useLastEventsForDevices = (enabled = true) => {
  const fleet = useFleet();

  return useQuery({
    queryKey: ['fleetHealth', fleet.id, 'lastEvents'],
    queryFn: () => FleetService.getLastEventsForDevices(),
    enabled: enabled && !!fleet.id && !!fleet.config.device_health_config?.is_enabled,
  });
};

export const useLastCheckInForDevices = (enabled = true) => {
  const fleet = useFleet();

  return useQuery({
    queryKey: ['fleetHealth', fleet.id, 'lastCheckIn'],
    queryFn: () => FleetService.getLastCheckInForDevices(),
    enabled: enabled && !!fleet.id && !!fleet.config.device_health_config?.is_enabled,
  });
};

export const useCumulativeUptimeForDevices = (enabled = true) => {
  const fleet = useFleet();

  return useQuery({
    queryKey: ['fleetHealth', fleet.id, 'cumulativeUptime'],
    queryFn: () => FleetService.getCumulativeUptimeForDevices(),
    enabled: enabled && !!fleet.id && !!fleet.config.device_health_config?.is_enabled,
  });
};

// Loading flags for each independently-hydrated section, so columns can render skeleton-first.
export interface FleetHealthSectionLoading {
  statuses: boolean;
  busFeed: boolean;
  otaDetails: boolean;
  versioning: boolean;
  lastEvents: boolean;
  lastCheckIn: boolean;
  cumulativeUptime: boolean;
  jiraTickets: boolean;
}

// useFleetHealthComposed replaces the monolithic useFleetHealth by fanning out to the split sub-endpoints
// in parallel and merging them back into the DeviceHealth shape, keyed by device id. The device rows come
// from the DB-backed /statuses endpoint so the table can paint before the Influx-backed sections resolve.
export const useFleetHealthComposed = (enabled = true) => {
  const statusesQuery = useHealthStatusesForDevices(enabled);
  const busFeedQuery = useBusFeedForDevices(enabled);
  const otaDetailsQuery = useOTADetailsForDevices(enabled);
  const versioningQuery = useVersioningForDevices(enabled);
  const lastEventsQuery = useLastEventsForDevices(enabled);
  const lastCheckInQuery = useLastCheckInForDevices(enabled);
  const cumulativeUptimeQuery = useCumulativeUptimeForDevices(enabled);
  const jiraTicketsQuery = useJiraTicketsForDevices(enabled);

  // Single source of truth for the sub-sections: adding a section here wires it into the per-section
  // loading flags and the skeleton-first gating below automatically. (The merge and its useMemo deps
  // below stay explicit because React's exhaustive-deps rule requires literal dependency arrays.)
  const sections = [
    { key: 'statuses', query: statusesQuery },
    { key: 'busFeed', query: busFeedQuery },
    { key: 'otaDetails', query: otaDetailsQuery },
    { key: 'versioning', query: versioningQuery },
    { key: 'lastEvents', query: lastEventsQuery },
    { key: 'lastCheckIn', query: lastCheckInQuery },
    { key: 'cumulativeUptime', query: cumulativeUptimeQuery },
    { key: 'jiraTickets', query: jiraTicketsQuery },
  ] as const;

  const data = useMemo(
    () => {
      const byId = new Map<string, DeviceHealth>();

      // Build the row set from the UNION of every section: each sub-endpoint returns the base device fields
      // (id, serial, vehicle, route, created_at) plus its own section, so any section can contribute a row.
      // This keeps rows visible even if one section (e.g. the slow/Influx-backed ones) is empty or lagging.
      // Absent JSON fields (omitempty) are simply not present, so spreading each partial is a safe merge.
      const mergeSection = (rows: Partial<DeviceHealth>[] | undefined) => {
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          if (!row.id) {
            return;
          }
          const existing = byId.get(row.id);
          byId.set(row.id, existing ? { ...existing, ...row } : (row as DeviceHealth));
        });
      };

      sections.forEach((section) => mergeSection(section.query.data));

      return Array.from(byId.values());
    },
    sections.map((section) => section.query.data),
  );

  const sectionLoading = Object.fromEntries(
    sections.map((section) => [section.key, section.query.isLoading]),
  ) as unknown as FleetHealthSectionLoading;

  // Show the loading overlay only until the FIRST section resolves; after that the table renders and the
  // remaining (incl. Influx-backed) sections hydrate their columns independently.
  const anySectionResolved = sections.some((section) => !section.query.isLoading);

  return {
    data,
    isLoading: !anySectionResolved,
    isError: statusesQuery.isError,
    sectionLoading,
  };
};

// _checkPeriod is accepted for backwards compatibility with existing call sites but is unused.
export const refreshFleetHealth = (fleetId: number, _checkPeriod?: number) => {
  const queryKey = ['fleetHealth', fleetId];
  queryClient.invalidateQueries({ queryKey });
};
